import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { student_routes } from "../../../router/all_routes";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { toast } from "sonner";
import { SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useAcademicYear } from "../../../../core/common/context/AcademicYearContext";
import { getAllEvaluationPeriodByLevel } from "../../../../api/generalParam";
import {
  Action,
  buildPermissionContext,
  Resource,
} from "../../../../core/common/autorisations/permissionsMap";
import { Select } from "antd";
import { getAllUEByPeriod } from "../../../../api/ue";
import { getAllStudentNoteByEvaluationPeriod } from "../../../../api/notes";
import { useSelector } from "react-redux";
import "./createClaim.css";
import { createStudentClaim } from "../../../../api/claim";

const CreateClaim = () => {
  const { selectedYear } = useAcademicYear();
  const userData = useSelector((state: any) => state.auth.user);
  const route = student_routes;

  const navigate = useNavigate();

  //Initialisation des state utilisés
  const [allEvaluationPeriod, setAllEvaluationPeriod] = useState<any[]>([]);
  const [selectedEvaluationPeriod, setSelectedEvaluationPeriod] =
    useState<any>(null);
  const [ecuList, setEcuList] = useState<any[]>([]);
  const [selectedECU, setSelectedECU] = useState<any>(null);
  const [allNotes, setAllNotes] = useState<any[]>([]);
  const [selectedNote, setSelectedNote] = useState<any>(null);
  const [notesBySubjectUE, setNotesBySubjectUE] = useState<
    Record<string, any[]>
  >({});

  //Schéma de notre formulaire
  const createSchema = z.object({
    id_note: z.string().trim().min(1, "La note est requise"),
    description: z.string().trim().min(1, "La description est requise"),
  });

  type FormFields = z.infer<typeof createSchema>;

  const {
    register,
    handleSubmit,
    clearErrors,
    setValue,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormFields>({
    resolver: zodResolver(createSchema),
    defaultValues: {
      id_note: "",
      description: "",
    },
  });

  //Traitement après validation du formulaire de création d'une réclamation
  const onSubmit: SubmitHandler<FormFields> = async (data) => {
    try {
      const payload = {
        id_note: data.id_note,
        description: data.description.trim(),
      };

      //Envoi des datas pour création vers le backend
      await createStudentClaim(payload);

      reset(); //On réinitialise les champs du formulaire
      setSelectedNote(null); // ← reset visuel du Select
      setSelectedECU(null);
      toast.success("Réclamation soumise !"); //Toast de succès

      navigate(route.myClaims); // Redirection vers la liste des réclamations
    } catch (error: any) {
      // Si erreur (Toast Error)
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  const fetchPeriods = async () => {
    if (!selectedYear?.Student?.Level?.id) {
      setAllEvaluationPeriod([]);
      setSelectedEvaluationPeriod(null);
      return;
    }
    try {
      const response = await getAllEvaluationPeriodByLevel(
        selectedYear?.Student?.Level?.id,
        buildPermissionContext(Resource.UE, Action.READ),
      );
      const periods = response.data || [];

      setAllEvaluationPeriod(periods);

      // Sélectionne la première période automatiquement s'il y en a
      if ((periods || []).length > 0) {
        setSelectedEvaluationPeriod(periods[0]);
      }
    } catch (error: any) {
      // console.log("erreur de chargement des périodes : ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
      // Option: reset les periodes en cas d'erreur
      setAllEvaluationPeriod([]);
    }
  };

  const fetchECUsByPeriod = async (periodId: string) => {
    if (!selectedYear?.Student?.Class?.id || !periodId) return [];
    try {
      const response = await getAllUEByPeriod(
        selectedYear?.Student?.Class?.id,
        periodId,
      );
      const ecus = response.data || [];
      return ecus.flatMap((ecu: any) =>
        Array.isArray(ecu?.SubjectUEs) ? ecu.SubjectUEs : [],
      );
    } catch (error: any) {
      // console.log("erreur de chargement des ECUs : ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
      return [];
    }
  };

  const fetchNotesByPeriod = async (periodId: string) => {
    if (!periodId || !selectedYear?.id || !userData?.id) {
      return {
        subjects: [],
        moyenne_generale: { value: null, total_notes: 0 },
      };
    }
    try {
      const response = await getAllStudentNoteByEvaluationPeriod(
        userData?.id,
        periodId,
        selectedYear?.id,
      );
      const studentNotes = response.data || [];
      return formatStudentNotes(studentNotes);
    } catch (error: any) {
      toast.error(
        error.response?.data?.message || "Erreur de chargement des notes",
      );
      return {
        subjects: [],
        moyenne_generale: { value: null, total_notes: 0 },
      };
    }
  };

  function formatStudentNotes(notes: any[]) {
    const subjectsMap: Record<string, any> = {};
    let totalNotes = 0;
    let countNotes = 0;

    notes.forEach((n: any) => {
      const subjectUEId = String(n?.id_subjectUE ?? "");
      const evaluationLevelId = String(n?.id_evaluationLevel ?? "");
      const evaluationId = String(n?.EvaluationLevel?.Evaluation?.id ?? "");
      const value = Number(n?.value);

      if (!subjectUEId || !evaluationLevelId) return;

      if (!subjectsMap[subjectUEId]) {
        subjectsMap[subjectUEId] = {
          id_subjectUE: subjectUEId,
          id_subject: n?.SubjectUE?.Subject?.id ?? null,
          subject: n?.SubjectUE?.Subject?.label ?? "",
          evaluations: [],
          moyenne_matiere: null as number | null,
        };
      }

      subjectsMap[subjectUEId].evaluations.push({
        id_note: n?.id ?? null,
        id_evaluationLevel: evaluationLevelId,
        id_evaluation: evaluationId || null,
        label: n?.EvaluationLevel?.Evaluation?.label ?? "",
        abbreviation: n?.EvaluationLevel?.Evaluation?.abbreviation ?? "",
        value: Number.isFinite(value) ? value : null,
      });

      if (Number.isFinite(value)) {
        totalNotes += value;
        countNotes += 1;
      }
    });

    const subjects = Object.values(subjectsMap).map((subject: any) => {
      const validValues = subject.evaluations
        .map((evaluation: any) => evaluation.value)
        .filter((v: any) => typeof v === "number");

      const moyenneMatiere =
        validValues.length > 0
          ? Number(
              (
                validValues.reduce((sum: number, v: number) => sum + v, 0) /
                validValues.length
              ).toFixed(2),
            )
          : null;

      return {
        ...subject,
        moyenne_matiere: moyenneMatiere,
      };
    });

    const moyenneGenerale =
      countNotes > 0 ? Number((totalNotes / countNotes).toFixed(2)) : null;

    return {
      subjects,
      moyenne_generale: {
        value: moyenneGenerale,
        total_notes: countNotes,
      },
    };
  }

  const getNoteTone = (value: any) => {
    const numericValue = Number(value);
    if (numericValue >= 10) return "good";
    if (numericValue >= 7) return "medium";
    return "bad";
  };

  useEffect(() => {
    fetchPeriods();
  }, [selectedYear?.Student?.Level?.id]);

  useEffect(() => {
    const classId = selectedYear?.Student?.Class?.id;
    const periodId = selectedEvaluationPeriod?.id;

    if (!classId || !periodId) {
      setEcuList([]);
      setNotesBySubjectUE({});
      setSelectedECU(null);
      setAllNotes([]);
      setSelectedNote(null);
      setValue("id_note", "");
      return;
    }

    let isActive = true;

    // Reset de la chaîne dépendante quand la période change
    setSelectedECU(null);
    setAllNotes([]);
    setSelectedNote(null);
    setValue("id_note", "");

    const loadPeriodData = async () => {
      const [subjectUEs, formattedNotes] = await Promise.all([
        fetchECUsByPeriod(String(periodId)),
        fetchNotesByPeriod(String(periodId)),
      ]);

      if (!isActive) return;

      setEcuList(subjectUEs);

      const notesMap: Record<string, any[]> = {};
      (formattedNotes.subjects || []).forEach((subject: any) => {
        notesMap[String(subject.id_subjectUE)] = subject.evaluations || [];
      });
      setNotesBySubjectUE(notesMap);
    };

    loadPeriodData();

    return () => {
      isActive = false;
    };
  }, [
    selectedYear?.Student?.Class?.id,
    selectedEvaluationPeriod?.id,
    selectedYear?.id,
    userData?.id,
  ]);

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          {/* /Page Header */}
          {selectedYear?.Student?.blocked_Note ? (
            <div className="card mb-4 claim-locked-card">
              {/* Bande rouge en haut */}
              <div className="claim-locked-strip" />

              <div className="card-body text-center py-5">
                {/* Icône dans un cercle */}
                <div className="mb-3 d-flex justify-content-center">
                  <div className="claim-locked-icon-wrap">
                    <i className="ti ti-lock-access text-danger claim-icon-lock" />
                  </div>
                </div>

                <h5 className="text-danger mb-2 fw-bold">
                  Accès aux réclamations bloqué
                </h5>

                <p className="text-muted mb-4 claim-locked-text">
                  Vos notes sont actuellement{" "}
                  <strong className="text-danger">bloquées</strong>. La
                  soumission de réclamation est indisponible pour votre profil.
                  <br />
                  Veuillez vous rapprocher de la scolarité pour régulariser
                  votre situation.
                </p>
                {/* Bouton contact */}
                <div className="d-flex justify-content-center gap-2 flex-wrap">
                  <span className="badge bg-danger-subtle text-danger px-3 py-2 claim-locked-pill">
                    <i className="ti ti-info-circle me-1" />
                    Contactez la scolarité
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <form
              id="claim-form"
              className="needs-validation"
              onSubmit={handleSubmit(onSubmit)}
            >
              <div className="card">
                <div className="card-header">
                  <h5 className="mb-0">Nouvelle Réclamation</h5>
                </div>
                <div className="card-body pb-0 row">
                  <div className="col-xxl-12 col-xl-12 col-md-12">
                    <div className="mb-2">
                      <label
                        className="form-label mb-1"
                        htmlFor="validationServer01"
                      >
                        Période d'évaluation
                      </label>
                      <Select
                        className="claim-select"
                        size="large"
                        optionFilterProp="label"
                        placeholder="Sélectionner une période d'évaluation"
                        value={selectedEvaluationPeriod?.id || undefined}
                        onChange={(periodId) => {
                          const nextPeriod = allEvaluationPeriod.find(
                            (p: any) => p.id === periodId,
                          );
                          setSelectedEvaluationPeriod(nextPeriod);
                          setSelectedECU(null); // reset la matière sélectionnée
                          setSelectedNote(null); // reset la note sélectionnée
                          setAllNotes([]);
                          setValue("id_note", "");
                          clearErrors("id_note");
                        }}
                      >
                        {allEvaluationPeriod.map((period: any) => (
                          <Select.Option
                            key={period.id}
                            value={period.id}
                            label={period.label ?? ""}
                          >
                            {period.label}
                          </Select.Option>
                        ))}
                      </Select>
                    </div>

                    {!selectedEvaluationPeriod?.publish_note ? (
                      // Notes pas encore publiées
                      <div className="card border-warning mb-4 bg-warning-subtle">
                        <div className="card-body text-center py-4">
                          <div className="mb-3">
                            <i className="ti ti-clock-pause text-warning claim-icon-md" />
                          </div>
                          <h5 className="text-warning mb-2">
                            Notes non publiées
                          </h5>
                          <p className="text-muted mb-0">
                            Les notes de cette période ne sont pas encore
                            disponibles.
                            <br />
                            La réclamation sera accessible dès leur publication.
                          </p>
                        </div>
                      </div>
                    ) : new Date() <
                      new Date(selectedEvaluationPeriod?.start_date_claim) ? (
                      // Fenêtre pas encore ouverte
                      <div className="card border-info mb-4 bg-info-subtle">
                        <div className="card-body text-center py-4">
                          <div className="mb-3">
                            <i className="ti ti-calendar-time text-info claim-icon-md" />
                          </div>
                          <h5 className="text-info mb-2">
                            Réclamations pas encore ouvertes
                          </h5>
                          <p className="text-muted mb-0">
                            La fenêtre de réclamation pour cette période n'a pas
                            encore débuté.
                            <br />
                            Elle sera disponible à partir du{" "}
                            <strong>
                              {new Date(
                                selectedEvaluationPeriod?.start_date_claim,
                              ).toLocaleDateString("fr-FR", {
                                day: "2-digit",
                                month: "long",
                                year: "numeric",
                              })}
                            </strong>
                            .
                          </p>
                        </div>
                      </div>
                    ) : new Date() >
                      new Date(selectedEvaluationPeriod?.end_date_claim) ? (
                      // Fenêtre fermée (date dépassée)
                      <div className="card border-danger mb-4 bg-danger-subtle">
                        <div className="card-body text-center py-4">
                          <div className="mb-3">
                            <i className="ti ti-lock text-danger claim-icon-md" />
                          </div>
                          <h5 className="text-danger mb-2">
                            Période de réclamation clôturée
                          </h5>
                          <p className="text-muted mb-0">
                            La fenêtre de réclamation pour cette période est
                            terminée depuis le{" "}
                            <strong>
                              {new Date(
                                selectedEvaluationPeriod?.end_date_claim,
                              ).toLocaleDateString("fr-FR", {
                                day: "2-digit",
                                month: "long",
                                year: "numeric",
                              })}
                            </strong>
                            .
                            <br />
                            Contactez la scolarité pour toute demande
                            exceptionnelle.
                          </p>
                        </div>
                      </div>
                    ) : (
                      // ✅ Fenêtre ouverte → afficher le formulaire
                      <>
                        <div className="alert alert-success py-2 px-3 mb-3 d-flex align-items-center gap-2 claim-open-alert">
                          <i className="ti ti-circle-check text-success claim-icon-sm" />
                          <span className="claim-open-alert-text">
                            Réclamations ouvertes jusqu'au{" "}
                            <strong>
                              {new Date(
                                selectedEvaluationPeriod?.end_date_claim,
                              ).toLocaleDateString("fr-FR", {
                                day: "2-digit",
                                month: "long",
                                year: "numeric",
                              })}
                            </strong>
                          </span>
                        </div>

                        <div className="mb-2">
                          <label className="form-label mb-1">Matière</label>
                          <Select
                            className="claim-select"
                            size="large"
                            optionFilterProp="label"
                            placeholder="Sélectionner une matière"
                            value={selectedECU?.id || undefined}
                            onChange={(ecuId) => {
                              const nextECU = ecuList.find(
                                (e: any) => e.id === ecuId,
                              );
                              setSelectedECU(nextECU);
                              setSelectedNote(null);
                              const notesForSubject = nextECU?.id
                                ? notesBySubjectUE[String(nextECU.id)] || []
                                : [];
                              setAllNotes(notesForSubject);
                              setValue("id_note", "");
                              clearErrors("id_note");
                            }}
                          >
                            {ecuList.map((ecu: any) => (
                              <Select.Option
                                key={ecu.id}
                                value={ecu.id}
                                label={ecu.label ?? ""}
                              >
                                {ecu.Subject?.label}
                              </Select.Option>
                            ))}
                          </Select>
                        </div>

                        <div className="mb-2">
                          <label className="form-label mb-1">
                            Note Réclamée
                          </label>
                          <input type="hidden" {...register("id_note")} />
                          <Select
                            className="claim-select"
                            size="large"
                            optionFilterProp="label"
                            placeholder="Sélectionner une note"
                            value={selectedNote?.id_note || undefined}
                            onChange={(noteId) => {
                              const nextNote = allNotes.find(
                                (n: any) => n.id_note === noteId,
                              );
                              setSelectedNote(nextNote);
                              setValue("id_note", String(noteId), {
                                shouldValidate: true,
                                shouldDirty: true,
                              }); // ← synchronise avec RHF
                              clearErrors("id_note"); // ← efface l'erreur si présente
                            }}
                          >
                            {allNotes.map((note: any) => (
                              <Select.Option
                                key={note.id_note}
                                value={note.id_note}
                                label={note.label ?? ""}
                              >
                                {note.label}
                              </Select.Option>
                            ))}
                          </Select>
                          {errors.id_note && (
                            <div className="invalid-feedback">
                              {errors.id_note.message}
                            </div>
                          )}

                          {selectedNote && (
                            <div className="claim-note-card">
                              <div className="claim-note-card__body">
                                <div className="claim-note-card__info">
                                  <div className="claim-note-card__title">
                                    {selectedNote.label}
                                    {selectedNote.abbreviation && (
                                      <span className="claim-note-card__abbr">
                                        {selectedNote.abbreviation}
                                      </span>
                                    )}
                                  </div>
                                  <div className="claim-note-card__progress-wrap">
                                    <div className="claim-note-card__progress-track">
                                      <div
                                        className={`claim-note-card__progress-fill claim-note-card__progress-fill--${getNoteTone(selectedNote.value)}`}
                                        style={{
                                          width: `${Math.min((Number(selectedNote.value) / 20) * 100, 100)}%`,
                                        }}
                                      />
                                    </div>
                                    <div className="claim-note-card__hint">
                                      {Number(selectedNote.value) >= 10
                                        ? "✅ Note suffisante"
                                        : "⚠️ Note insuffisante"}
                                    </div>
                                  </div>
                                </div>
                                <div className="claim-note-card__divider" />
                                <div
                                  className={`claim-note-card__score claim-note-card__score--${getNoteTone(selectedNote.value)}`}
                                >
                                  <div
                                    className={`claim-note-card__value claim-note-card__value--${getNoteTone(selectedNote.value)}`}
                                  >
                                    {selectedNote.value !== null &&
                                    selectedNote.value !== undefined
                                      ? String(
                                          Number(selectedNote.value).toFixed(2),
                                        ).padStart(5, "0")
                                      : "N/A"}
                                  </div>
                                  <div className="claim-note-card__over">
                                    / 20
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="mb-2">
                          <label className="form-label mb-1">Motif</label>
                          <textarea
                            {...register("description")}
                            className={
                              errors.description
                                ? "form-control is-invalid"
                                : "form-control"
                            }
                            placeholder="Description détaillée de la réclamation"
                          />
                          {errors.description && (
                            <div className="invalid-feedback">
                              {errors.description.message}
                            </div>
                          )}
                        </div>
                        <div className="mb-2 mt-4">
                          {isSubmitting ? (
                            <button
                              className="btn btn-primary-light"
                              type="button"
                              disabled={false}
                            >
                              <span
                                className="spinner-border spinner-border-sm align-middle me-1"
                                role="status"
                                aria-hidden="true"
                              />
                              Chargement...
                            </button>
                          ) : (
                            <button
                              type="submit"
                              form="claim-form" // 🔗 liaison au formulaire
                              className="btn btn-primary"
                              disabled={isSubmitting}
                            >
                              Soumettre la réclamation
                            </button>
                          )}
                          <Link
                            to={route.myClaims}
                            className="btn btn-danger ms-3"
                          >
                            Annuler
                          </Link>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </form>
          )}
        </div>
      </div>
    </>
  );
};

export default CreateClaim;
